import { useState } from 'react';
import { Button, Modal, Stack, useTheme } from '@fti/design-system';
import { useRegenerateStaticReflection } from '../../../api/media-sim/reflections';

interface RegenerateButtonProps {
  publicationId: string;
  isAdmin?: boolean;
  onRegenerated?: () => void;
}

export function RegenerateButton({ publicationId, onRegenerated }: RegenerateButtonProps) {
  const { theme } = useTheme();
  const { regenerate, loading } = useRegenerateStaticReflection();
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setError(null);
    try {
      await regenerate(publicationId);
      setModalOpen(false);
      onRegenerated?.();
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      if (apiErr?.status === 409) {
        setError('Generation already in progress for this publication.');
      } else {
        setError(apiErr?.message || 'Failed to start regeneration.');
      }
    }
  };

  return (
    <>
      <Button variant="outline" color="secondary" onClick={() => setModalOpen(true)}>
        Regenerate Reflection
      </Button>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        <Modal.Header>
          <h2 style={{ margin: 0, color: theme.colors.text.primary }}>Regenerate Static Reflection</h2>
        </Modal.Header>
        <Modal.Body>
          <Stack gap="sm">
            <p style={{ color: theme.colors.text.primary, margin: 0 }}>
              This will re-analyze articles and generate a new publication style profile.
              The current reflection will be replaced.
            </p>
            {error && (
              <p style={{ color: theme.colors.semantic.error.default, margin: 0 }}>{error}</p>
            )}
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="solid" color="primary" onClick={handleConfirm} loading={loading}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
